import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addLead, updateLead } from "../../../state/reducers/leadSlice";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { createLead } from "../../../services/admin.services";

const ImportLeadExcel = () => {
  const dispatch = useDispatch();
  const existingLeads = useSelector((state) => state.leads.leads);
  const [file, setFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [parsedLeads, setParsedLeads] = useState([]);
  const [status, setStatus] = useState("");
  const [leadType, setLeadType] = useState("");
  const [assigned, setAssigned] = useState("");

  const admins = useSelector((state) => state.admin.admins);

  /**
   * Handles file upload, parses the Excel file, and normalizes the data.
   */
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const normalizedData = normalizeKeys(jsonData);

        setParsedLeads(normalizedData);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  /**
   * Normalizes the keys of the uploaded Excel data to match Redux fields.
   */
  const normalizeKeys = (data) => {
    return data.map((row) => {
      const normalizedRow = Object.keys(row).reduce((acc, key) => {
        const normalizedKey = key.toLowerCase().replace(/\s+/g, "_");
        acc[normalizedKey] = row[key];
        return acc;
      }, {});

      normalizedRow.created_at = normalizedRow.created_at || "N/A";
      normalizedRow.branch = normalizedRow.branch || "Unknown";

      return normalizedRow;
    });
  };

  /**
   * Downloads a sample Excel file with dummy data.
   */
  const downloadSampleFile = () => {
    const sampleData = [
      {
        Name: "Alice Johnson",
        Phone: "1234567890",
        Status: "Fresh",
        "Lead Type": "Career Counseling",
        "Contacted At": "2024-01-01",
        "Created At": "2024-01-01",
        Branch: "Andheri",
        Email: "alice@example.com",
        Address: "123 Main Street",
        Notes: "Follow-up required",
      },
      {
        Name: "Bob Smith",
        Phone: "0987654321",
        Status: "Enquiry",
        "Lead Type": "Abroad Counseling",
        "Contacted At": "2024-01-02",
        "Created At": "2024-01-02",
        Branch: "Vidhyavihar",
        Email: "bob@example.com",
        Address: "456 Elm Street",
        Notes: "Interested in package",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Leads");

    XLSX.writeFile(workbook, "Sample_Leads.xlsx");
  };

  /**
   * Updates and downloads Excel file with parsed and modified leads.
   */
  const handleUpdateAndDownload = () => {
    if (!parsedLeads.length) {
      alert("Please upload and parse an Excel file first.");
      return;
    }

    const updatedLeads = parsedLeads.map((lead) => ({
      ...lead,
      status: status || lead.status || "fresh",
      lead_type: leadType || lead.lead_type || "abroad_counselling",
      assigned: assigned || lead.assigned,
    }));

    const worksheet = XLSX.utils.json_to_sheet(updatedLeads);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Updated Leads");

    XLSX.writeFile(workbook, "Updated_Leads.xlsx");

    alert("Excel updated and downloaded as 'Updated_Leads.xlsx'.");
  };

  /**
   * Resets file and parsed leads data.
   */
  const handleFileDelete = () => {
    setUploadedFileName(null);
    setParsedLeads([]);
    setStatus("");
    setLeadType("");
    setAssigned("");
  };

  /**
   * Submits parsed leads to Redux store.
   */
  const handleFileSubmit = async () => {
    if (parsedLeads.length === 0) {
      toast.error("No leads to process.");
      return;
    }

    const validateLead = (lead) => lead.name && lead.phone;

    for (const newLead of parsedLeads) {
      if (validateLead(newLead)) {
        const formattedLead = {
          ...newLead,
          subjects_liked:
            typeof newLead.subjects_liked === "string"
              ? newLead.subjects_liked
              : "",
          subjects_disliked:
            typeof newLead.subjects_disliked === "string"
              ? newLead.subjects_disliked
              : "",
        };

        try {
          // Create new lead
          await createLead(formattedLead);
          toast.success(`Lead "${newLead.name}" created successfully!`);
        } catch (error) {
          console.error(
            "Error creating lead:",
            error.response?.data || error.message
          );
          toast.error(
            `Failed to create lead "${newLead.name}". Please check the input.`
          );
        }
      } else {
        console.error("Invalid lead data (Name and Phone required):", newLead);
      }
    }

    // fetchLeads(); // Refresh the leads list
    setUploadedFileName(null);
    setParsedLeads([]);
    toast.success("All leads created successfully!");
  };

  return (
    <div className="p-6 font-sans max-w-screen-lg m-auto">
      <h2 className="text-2xl font-semibold mb-4">Import Leads</h2>
      <button
        onClick={downloadSampleFile}
        className="bg-blue-500 text-white px-4 py-2 rounded mb-4"
      >
        Download Sample
      </button>
      <p className="mb-2">
        1. Your CSV data should be in the format below. The first line of your
        CSV file should be the column headers as in the table example. Also,
        make sure that your file is UTF-8 to avoid unnecessary encoding
        problems. <br />
        All fields are not Compulsory except Name and Phone.The rest can be
        empty
      </p>
      <p className="mb-2">
        2. If the column you are trying to import is date, make sure that it is
        formatted in <b>YYYY-MM-DD</b> format.
      </p>
      <p className="mb-4">
        3. Based on your leads <b>unique validation</b> configured options, the
        lead won’t be imported if:
      </p>
      <ul className="list-disc ml-6 mb-4">
        <li>Lead phone number already exists</li>
      </ul>
      <p className="mb-4">
        If you still want to import all leads, uncheck all unique validation
        fields.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full max-h-[500px] border-collapse border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Name
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Phone
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Status
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Lead Type
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Contacted At
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Created At
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Branch
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Assigned To
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Email
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Address
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Notes
              </th>
            </tr>
          </thead>
          <tbody>
            {parsedLeads.length > 0 ? (
              parsedLeads.map((lead, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                >
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.name}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.phone}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.status}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.lead_type}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.contacted_at || "N/A"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.created_at || "N/A"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.branch}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.assigned || "N/A"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.email}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.address}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {lead.notes}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="11"
                  className="border border-gray-300 px-4 py-2 text-center"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mb-4">
        <label className="block mb-2 font-medium">Choose Excel File:</label>
        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          className="border border-gray-300 rounded px-4 py-2 w-full"
        />
      </div>
      {uploadedFileName && (
        <div className="mb-4">
          <p>
            <b>Uploaded File:</b> {uploadedFileName}
          </p>
          <button
            onClick={handleFileDelete}
            className="bg-red-500 text-white px-4 py-2 rounded mt-2"
          >
            Remove File
          </button>
        </div>
      )}
      <div className="mb-4">
        <label className="block mb-2 font-medium">Select Status:</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border border-gray-300 rounded px-4 py-2 w-full"
        >
          <option value="">No Change</option>
          <option value="Fresh">Fresh</option>
          <option value="Enquiry">Enquiry</option>
          <option value="Customer">Customer</option>
        </select>
      </div>
      <div className="mb-4">
        <label className="block mb-2 font-medium">Select Lead Type:</label>
        <select
          value={leadType}
          onChange={(e) => setLeadType(e.target.value)}
          className="border border-gray-300 rounded px-4 py-2 w-full"
        >
          <option value="">No Change</option>
          <option value="abroad_counseling">Abroad Counseling</option>
          <option value="career_counseling">Career Counseling</option>
          <option value="subject_selection">Subject Selection</option>
          <option value="test_prep">Test Prep</option>
          <option value="visa">Visa Process</option>
          <option value="others">Others</option>
        </select>
      </div>
      <div className="mb-4">
        <label className="block mb-2 font-medium">Assign To:</label>
        <select
          value={assigned}
          onChange={(e) => setAssigned(e.target.value)}
          className="border border-gray-300 rounded px-4 py-2 w-full"
        >
          <option value="">No Change</option>
          {admins
            .filter((admin) => admin.role === "admin")
            .map((admin) => (
              <option value={admin.id}>{admin.username}</option>
            ))}
        </select>
      </div>
      <button
        onClick={handleUpdateAndDownload}
        className="bg-blue-500 text-white px-6 py-2 rounded mr-4"
      >
        Update and Download Excel
      </button>
      <button
        onClick={handleFileSubmit}
        className="bg-gray-500 text-white px-6 py-2 rounded"
      >
        Import
      </button>
    </div>
  );
};

export default ImportLeadExcel;
